import React from 'react';
import { Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

type SwitchSize = 'default' | 'small';
type SwitchState = 'enabled' | 'disabled';

export interface SwitchProps {
  label?: string;
  showLabel?: boolean;
  selected?: boolean;
  size?: SwitchSize;
  state?: SwitchState;
  onPress?: () => void;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

export function Switch({
  label = 'Label',
  showLabel = false,
  selected = true,
  size = 'default',
  state = 'enabled',
  onPress,
}: SwitchProps) {
  const theme = useTheme();
  const isSmall = size === 'small';
  const isDisabled = state === 'disabled';
  const isInteractive = !isDisabled;

  const trackWidth = isSmall ? 48 : 56;
  const trackHeight = isSmall ? 24 : 32;
  const knobInset = isSmall ? 2 : 4;
  const knobSize = isSmall ? 20 : 24;

  const labelVariant = isSmall ? 'bodySmall' : 'bodyBase';

  function getTrackColor() {
    if (isDisabled) return theme.colors['surface.disabled'];
    if (selected) return theme.colors['surface.brand.default'];
    return theme.colors['surface.secondary'];
  }

  function getTrackBorderColor() {
    if (isDisabled) return theme.colors['border.secondary'];
    if (selected && isInteractive) return undefined;
    return theme.colors['border.secondary'];
  }

  function getKnobColor() {
    if (isDisabled) return theme.colors['surface.disabled'];
    return theme.colors.white;
  }

  function getLabelColor() {
    if (isDisabled) return theme.colors['text.disabled'];
    return selected ? theme.colors['text.brand'] : theme.colors['text.secondary'];
  }

  function getKnobPosition() {
    if (selected) {
      return {
        right: knobInset,
      } as const;
    }

    return {
      left: knobInset,
    } as const;
  }

  function renderInner(interaction?: PressableInteractionState) {
    const pressed = interaction?.pressed ?? false;
    const hovered = interaction?.hovered ?? false;

    const showPressedOverlay = isInteractive && pressed;
    const showHoverOverlay = isInteractive && !pressed && hovered;
    const overlayColor = showPressedOverlay
      ? theme.colors['state-layer.press-brand']
      : showHoverOverlay
        ? theme.colors['state-layer.hover-brand']
        : undefined;

    return (
      <Box flexDirection="row" alignItems="center" gap="spacing.general.x2">
        {showLabel ? (
          <Text variant={labelVariant} style={{ color: getLabelColor() }}>
            {label}
          </Text>
        ) : null}

        <Box
          position="relative"
          overflow="hidden"
          borderRadius="radius.full"
          width={trackWidth}
          height={trackHeight}
          borderWidth={isDisabled || !selected ? 1 : 0}
          style={{
            borderColor: getTrackBorderColor(),
            backgroundColor: getTrackColor(),
          }}
        >
          {overlayColor ? (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius="radius.full"
              style={{ backgroundColor: overlayColor }}
            />
          ) : null}

          <Box
            position="absolute"
            top={knobInset}
            width={knobSize}
            height={knobSize}
            borderRadius="radius.full"
            style={{
              backgroundColor: getKnobColor(),
              ...getKnobPosition(),
            }}
          />
        </Box>
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Box>{renderInner()}</Box>;
}
